import type React from 'react';
import {useState} from 'react';
import {Box, Button, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography} from '@mui/material';
import {Add} from '@mui/icons-material';
import {Tag} from '~/components/tag/Tag';
import type {MarketActions} from '../../hooks/useMarket';
import type {MarketDriver, MarketIndex} from '../../types/marketTypes';
import {DriverDialog, type DriverDraft} from './DriverDialog';
import {NumberCell} from '../strategy/tools/NumberCell';
import {RowAction} from '../components/RowAction';
import {LOCALE} from '~/pages/category-profile/domain/locale';

const PROVIDER = {eurostat: 'Eurostat', ecb: 'ECB', databento: 'Databento', manual: 'Uploaded'} as Record<string, string>;

const draftOf = (d: MarketDriver): DriverDraft => ({
    name: d.series && d.name === d.series.name ? '' : d.name, seriesId: d.series?.id ?? null, unit: d.unit, weight: d.weight,
    prices: d.prices ?? [],
});

/**
 * The category's price drivers (shared with the Should cost tab): confirmed ones with their weight, suggestions to
 * confirm, add and edit. A driver follows an index or is priced by hand per month.
 */
export const DriversSection: React.FC<{data: MarketIndex; actions: MarketActions}> = ({data, actions}) => {
    const [editing, setEditing] = useState<MarketDriver | 'new' | null>(null);
    const choosable = data.catalogue.filter(s => s.freshness !== 'empty');
    const fmt = (v: number | null, digits = 2) => (v === null ? '—' : v.toLocaleString(LOCALE, {maximumFractionDigits: digits}));

    return (
        <Stack gap={1.5}>
            <Box sx={{overflowX: 'auto'}}>
                <Table size="small" aria-label="Cost drivers">
                    <TableHead>
                        <TableRow>
                            <TableCell>Driver</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Weight %</TableCell>
                            <TableCell align="right">Latest</TableCell>
                            <TableCell align="right">YoY</TableCell>
                            <TableCell align="right">r vs price</TableCell>
                            <TableCell align="right">Sensitivity</TableCell>
                            <TableCell/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.drivers.length === 0 && (
                            <TableRow><TableCell colSpan={8}>
                                <Typography variant="body2" color="text.secondary">No drivers yet. Add one below.</Typography>
                            </TableCell></TableRow>
                        )}
                        {data.drivers.map(d => (
                            <TableRow key={d.mappingId ?? `suggested-${d.series?.id}`}>
                                <TableCell>
                                    <Typography variant="body2">{d.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {d.series
                                            ? `${PROVIDER[d.series.provider] ?? d.series.provider} · ${d.series.unit}${d.name !== d.series.name ? ` · ${d.series.name}` : ''}`
                                            : `Prices entered by hand${d.unit ? ` · ${d.unit}` : ''}`}
                                        {d.freshness === 'stale' && ' · stale'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Tag size="small" solid={d.status === 'confirmed'} color={d.status === 'confirmed' ? 'success' : 'default'}
                                         label={d.status === 'confirmed' ? 'Confirmed' : 'Suggested'}/>
                                </TableCell>
                                <TableCell align="right">
                                    {d.status === 'confirmed' ? (
                                        <NumberCell value={Math.round(d.weight * 100)} min={0} max={100} disabled={!data.canEdit}
                                                    label={`Weight of ${d.name}`} onCommit={v => void actions.setWeight(d, v / 100)}/>
                                    ) : `${Math.round(d.weight * 100)}`}
                                </TableCell>
                                <TableCell align="right">{fmt(d.latestValue)}</TableCell>
                                <TableCell align="right">{d.yoyPct === null ? '—' : `${d.yoyPct > 0 ? '+' : ''}${d.yoyPct.toFixed(1)}%`}</TableCell>
                                <TableCell align="right">
                                    {d.r === null ? (
                                        <Tooltip title="Needs 6 months of price paid and driver data"><span>—</span></Tooltip>
                                    ) : (
                                        <Tooltip title={d.weak ? `Weak link: below ${data.correlationWarning}` : `Best at a ${d.lagMonths}-month lag`}>
                                            <Typography variant="body2" component="span" color={d.weak ? 'warning.dark' : 'text.primary'}>
                                                {`${d.r.toFixed(2)}${d.lagMonths ? ` (${d.lagMonths}m lag)` : ''}`}
                                            </Typography>
                                        </Tooltip>
                                    )}
                                </TableCell>
                                <TableCell align="right">{fmt(d.sensitivity)}</TableCell>
                                <TableCell align="right">
                                    {data.canEdit && (d.status === 'suggested'
                                        ? <Button size="small" onClick={() => void actions.confirm(d)}>Confirm</Button>
                                        : <Stack direction="row" justifyContent="flex-end">
                                            <RowAction kind="edit" label={d.name} onClick={() => setEditing(d)}/>
                                            <RowAction kind="delete" label={d.name} onClick={() => void actions.remove(d)}/>
                                        </Stack>)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
            {data.canEdit && (
                <Button size="small" startIcon={<Add/>} sx={{alignSelf: 'flex-start'}} onClick={() => setEditing('new')}>Add a driver</Button>
            )}
            {editing && (
                <DriverDialog driver={editing === 'new' ? undefined : draftOf(editing)} series={choosable} onClose={() => setEditing(null)}
                              onSubmit={input => editing === 'new' ? actions.add(input) : actions.update(editing.mappingId!, input)}/>
            )}
            <Typography variant="caption" color="text.secondary">
                Suggested drivers come from the category name and stay out of the driver mix until confirmed. A driver
                priced by hand is analysed on its monthly prices. The Should cost tab uses the same drivers. r is the
                correlation of price paid with the driver (best of 0-3 months lag); sensitivity 0.3 means a 10% driver
                move went with a 3% price move.
            </Typography>
        </Stack>
    );
};
